import React from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Snapshot } from './lib/types';
import { ActionRow } from './lib/selectors';
import { whyOf } from './lib/why';
import { fmtIct } from './lib/ict';
import { ProvLine, VerdictChip } from './chips';
import { FONT, OVERLINE, RADIUS } from './lib/brand';

/**
 * "Why is this here?" for one item (DASHBOARD.md §1.18): the scanned source
 * excerpt, the exec's reasoning with provenance, and the ledger ids found in the
 * text. The excerpt is plain text in a <pre>, never markup; one that tripped the
 * secret scan reads "withheld" and its text was never shipped.
 */
export function WhyDialog({ row, snapshot, onClose, onGa }: {
  row: ActionRow | null; snapshot: Snapshot; onClose: () => void; onGa: (id: string) => void;
}) {
  const a = row && row.action;
  const w = a ? whyOf(a, snapshot.ledger || []) : null;
  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="why-title" data-testid="why-dialog">
      {row && a && w && (
        <>
          <DialogTitle id="why-title" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700 }}>
            Why is this here?
            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.3 }}>{a.for || a.text || 'Untitled item'} · {row.bot.name}</Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="overline" sx={{ ...OVERLINE }}>Source excerpt</Typography>
            {w.excerpt.state === 'shown' && (
              <Box data-testid="why-excerpt" component="pre" sx={{ m: 0, my: 0.6, p: 1.2, bgcolor: 'action.hover', borderRadius: RADIUS.md, fontFamily: FONT.mono, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflow: 'auto' }}>
                {w.excerpt.text}
              </Box>
            )}
            {w.excerpt.state === 'withheld' && (
              <Typography data-testid="why-withheld" variant="body2" color="text.secondary" sx={{ my: 0.6 }}>
                Withheld: the excerpt looked like it contained a secret or a personal email, so Grok Bot did not send it. Open the source file to read it.
              </Typography>
            )}
            {w.excerpt.state === 'none' && (
              <Typography data-testid="why-no-excerpt" variant="body2" color="text.secondary" sx={{ my: 0.6 }}>Not stated in source: no excerpt was recorded for this item.</Typography>
            )}
            <ProvLine prov={w.excerpt.prov} fmt={fmtIct} />
            {w.excerpt.lines && <Typography variant="caption" color="text.secondary">Lines {w.excerpt.lines}</Typography>}

            <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 2 }}>Exec reasoning</Typography>
            {w.reasoning.text ? (
              <Box data-testid="why-reasoning" sx={{ my: 0.6 }}>
                {w.reasoning.exec && w.reasoning.verdict && <Box sx={{ mb: 0.6 }}><VerdictChip exec={w.reasoning.exec} verdict={w.reasoning.verdict as 'agree' | 'alternative'} /></Box>}
                <Typography sx={{ fontSize: 13.5 }}>{w.reasoning.text}</Typography>
                <ProvLine prov={w.reasoning.prov} fmt={fmtIct} />
              </Box>
            ) : (
              <Typography data-testid="why-no-reasoning" variant="body2" color="text.secondary" sx={{ my: 0.6 }}>No C-suite review has been recorded for this item yet.</Typography>
            )}

            <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', mt: 2 }}>Linked ids</Typography>
            {w.linked.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ my: 0.6 }}>None found in the text.</Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', my: 0.8 }}>
                {w.linked.map(({ id, row: l }) => (l
                  ? <Chip key={id} clickable size="small" color="primary" variant="outlined" label={id} title={l.title} onClick={() => { onClose(); onGa(id); }} />
                  : <Chip key={id} size="small" variant="outlined" label={`${id} (not in the ledger)`} />))}
              </Box>
            )}
          </DialogContent>
          <DialogActions><Button onClick={onClose} autoFocus>Close</Button></DialogActions>
        </>
      )}
    </Dialog>
  );
}
